import { ArrowLeftRight, LogOut, Menu, Pencil, Shield, User, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { apiFetch } from "../../lib/api.js";
import { signOut } from "../../lib/auth-client.js";
import { AccountAvatar, useDismissPopover, usePopover } from "./account-avatar.js";
import { HeaderGlyph } from "./glyph.js";

interface MeUser {
  id: string;
  email: string;
  name: string | null;
  image: string | null;
}

interface MeOrg {
  id: string;
  name: string;
  slug: string;
  logo: string | null;
  role: string;
}

interface MeTeamLite {
  id: string;
  name: string;
}

interface Me {
  user: MeUser;
  organization: MeOrg | null;
  organizations: MeOrg[];
  activeTeam?: MeTeamLite | null;
  teams?: MeTeamLite[];
}

/**
 * Glass-floating dropdown anchored to the header avatar. Surfaces the
 * actions every header should expose:
 *
 *   1. Konto verwalten      → /account
 *   2. Verein bearbeiten    → /club
 *   3. Verein wechseln      → /select/org (only when ≥2 memberships)
 *   4. Team bearbeiten      → /team
 *   5. Team wechseln        → /select/team (only when ≥2 reachable teams)
 *   6. Abmelden             → Better Auth sign-out + token wipe + reload
 *
 * The "wechseln" actions deliberately don't inline a picker — multi-org
 * coaches confirmed they prefer dedicated full-screen pages so the choice
 * feels intentional and matches the post-login flow.
 */
export function AccountMenu() {
  const navigate = useNavigate();
  const popover = usePopover();
  const ref = useDismissPopover(popover.open, popover.close);
  const me = useMe();

  if (!me) {
    return (
      <HeaderGlyph label="Menü" onClick={popover.toggle}>
        <Menu className="size-5" aria-hidden />
      </HeaderGlyph>
    );
  }

  const user = me.user;
  const current = me.organization ?? me.organizations[0] ?? null;
  const multiClub = me.organizations.length > 1;
  const activeTeam = me.activeTeam ?? null;
  const multiTeam = (me.teams?.length ?? 0) > 1;

  async function handleSignOut(): Promise<void> {
    try {
      await signOut();
    } finally {
      localStorage.removeItem("coach.license.token");
      localStorage.removeItem("coach.bearer");
      localStorage.removeItem("coach.me.v1");
      localStorage.removeItem("coach.sel.confirmedOrgId");
      localStorage.removeItem("coach.sel.confirmedTeamId");
      localStorage.removeItem("coach.sel.lastUserId");
      window.location.reload();
    }
  }

  return (
    <div ref={ref} className="relative">
      <HeaderGlyph label="Menü" pressed={popover.open} onClick={popover.toggle}>
        <Menu className="size-5" aria-hidden />
      </HeaderGlyph>

      {popover.open && (
        <div
          role="menu"
          aria-label="Konto"
          className="absolute right-0 z-50 mt-2 w-72 overflow-hidden rounded-2xl border border-white/10 bg-background/95 shadow-floating backdrop-blur-floating"
        >
          <header className="flex items-center gap-3 border-b border-white/10 px-4 py-3">
            <AccountAvatar name={user.name} email={user.email} image={user.image} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-body-md font-semibold">
                {user.name ?? user.email.split("@")[0]}
              </div>
              <div className="truncate text-label-caps uppercase text-muted-foreground">
                {user.email}
              </div>
            </div>
          </header>

          <ul className="flex flex-col py-1">
            <MenuRow
              Icon={User}
              label="Konto verwalten"
              description="Profil, Foto, Passwort"
              onClick={() => {
                popover.close();
                navigate("/account");
              }}
            />
            {current && (
              <MenuRow
                Icon={Shield}
                label="Verein"
                description={
                  current.role === "owner" ? "Vereinsdaten & Logo" : "Vereinsdaten ansehen"
                }
                onClick={() => {
                  popover.close();
                  navigate("/club");
                }}
              />
            )}
            {multiClub && current && (
              <MenuRow
                Icon={ArrowLeftRight}
                label="Verein wechseln"
                description={current.name}
                onClick={() => {
                  popover.close();
                  navigate("/select/org");
                }}
              />
            )}
            {activeTeam && (
              <MenuRow
                Icon={Pencil}
                label="Team bearbeiten"
                description={activeTeam.name}
                onClick={() => {
                  popover.close();
                  navigate("/team");
                }}
              />
            )}
            {multiTeam && (
              <MenuRow
                Icon={Users}
                label="Team wechseln"
                description={activeTeam?.name ?? "Team wählen"}
                onClick={() => {
                  popover.close();
                  navigate("/select/team");
                }}
              />
            )}
            <div className="my-1 h-px bg-white/10" />
            <MenuRow
              Icon={LogOut}
              label="Abmelden"
              description="Sitzung beenden"
              tone="danger"
              onClick={() => {
                popover.close();
                void handleSignOut();
              }}
            />
          </ul>
        </div>
      )}
    </div>
  );
}

function MenuRow({
  Icon,
  label,
  description,
  onClick,
  tone,
}: {
  Icon: typeof User;
  label: string;
  description?: string;
  onClick: () => void;
  tone?: "danger";
}) {
  const accent = tone === "danger" ? "var(--color-tactical-red)" : "var(--color-on-surface)";
  return (
    <li>
      <button
        type="button"
        role="menuitem"
        onClick={onClick}
        className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-white/6"
      >
        <span
          className="grid size-8 place-items-center rounded-lg border"
          style={{
            borderColor: `color-mix(in srgb, ${accent} 25%, transparent)`,
            background: `color-mix(in srgb, ${accent} 12%, transparent)`,
            color: accent,
          }}
        >
          <Icon className="size-4" aria-hidden />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span
            className="truncate text-body-sm font-medium"
            style={tone === "danger" ? { color: accent } : undefined}
          >
            {label}
          </span>
          {description && (
            <span className="truncate text-label-caps uppercase text-muted-foreground">
              {description}
            </span>
          )}
        </span>
      </button>
    </li>
  );
}

/**
 * Lightweight /me fetcher with module-level caching so multiple header
 * instances on the same screen don't trigger duplicate network calls.
 *
 * Re-fetched on mount; sufficient because the header lives at the very top
 * of the route tree (one mount per app session).
 */
let cachedMe: Me | null = null;
function useMe(): Me | null {
  const [me, setMe] = useState<Me | null>(cachedMe);
  useEffect(() => {
    let cancelled = false;
    apiFetch<Me>("/api/v1/me", { credentials: "include" })
      .then((m) => {
        cachedMe = m;
        if (!cancelled) setMe(m);
      })
      .catch(() => {
        /* offline — keep whatever we had */
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return me;
}
